import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { Locale } from "../../../shared/types";
import type {
  CreatedInvite,
  InviteSummary,
  ParticipantRow,
  ParticipantsResponse,
  ParticipantTranscript,
  StudySummary,
} from "../lib/api";
import * as api from "../lib/api";
import { useCopy } from "../lib/copy";
import type { StudyShell } from "./Studies";
import Synthesis from "./Synthesis";

/**
 * The live board for one study.
 *
 * This is the screen the researcher leaves open. Interviews land on it while they do
 * something else, and that is the whole delight: nothing else in the category fills
 * in front of you, they all make you press Generate. So the rows arrive on their own,
 * a landing row is the one moment of motion, and every number on the board clicks
 * through to a person saying a thing — a count you cannot open is a dashboard, which
 * is the anti-goal (PLATFORM-W4.md 6.3).
 */

interface StudyBoardProps {
  locale: Locale;
  studyId: number;
  shell: StudyShell;
  /**
   * The link, handed over exactly once, straight after creation. It cannot be
   * fetched again: the server stores sha256(token) and nothing else, which is why
   * the screen says so rather than offering a button that cannot work.
   */
  freshInvite: CreatedInvite | null;
  onInviteDropped: () => void;
  onBack: () => void;
  onChanged: (study: StudySummary) => void;
}

/**
 * How often the board asks who has answered. Eight seconds is slow enough to be free
 * and quick enough that a row lands while you are still looking at the screen. Paused
 * while the tab is hidden, because nobody is watching it fill.
 */
const POLL_MS = 8000;

/** How long a newly arrived row keeps its entrance class. Matches --d-base plus slack. */
const LAND_MS = 700;

export default function StudyBoard({
  locale,
  studyId,
  shell,
  freshInvite,
  onInviteDropped,
  onBack,
  onChanged,
}: StudyBoardProps) {
  const copy = useCopy(locale);

  const [study, setStudy] = useState<StudySummary | null>(null);
  const [board, setBoard] = useState<ParticipantsResponse | null>(null);
  const [invite, setInvite] = useState<CreatedInvite | null>(freshInvite);
  /** Every link that exists, WITHOUT its token. Uses, expiry, and whether it is dead. */
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [transcripts, setTranscripts] = useState<Record<number, ParticipantTranscript>>({});
  const [working, setWorking] = useState(false);
  /** The ids that arrived since the last poll. They are the only things that move. */
  const [landed, setLanded] = useState<number[]>([]);
  const seenRef = useRef<Set<number> | null>(null);

  const { setBusy, say } = shell;

  useEffect(() => {
    setInvite(freshInvite);
  }, [freshInvite]);

  useEffect(() => {
    const controller = new AbortController();
    setBusy(copy.thinking);
    void (async () => {
      try {
        const detail = await api.getStudy(studyId, controller.signal);
        if (!controller.signal.aborted) setStudy(detail);
      } catch (err) {
        if (!controller.signal.aborted) say(api.describe(err, copy.studyOpenFailed), "error");
      } finally {
        if (!controller.signal.aborted) setBusy(null);
      }
    })();
    return () => {
      controller.abort();
      setBusy(null);
    };
  }, [copy.studyOpenFailed, copy.thinking, say, setBusy, studyId]);

  const readInvites = useCallback((): void => {
    void (async () => {
      try {
        setInvites(await api.listInvites(studyId));
      } catch {
        // The links section simply lists nothing. The board is not about them.
      }
    })();
  }, [studyId]);

  useEffect(readInvites, [readInvites]);

  const status = study?.status ?? null;

  // Who has answered, and whether there is enough of it. Read once on arrival and
  // then on a timer for as long as the study is in the field. A failed poll says
  // nothing: the board is already on screen and one dropped request is not news.
  useEffect(() => {
    if (status === null) return;
    let alive = true;
    const read = async (): Promise<void> => {
      try {
        const next = await api.listParticipants(studyId);
        if (alive) setBoard(next);
      } catch {
        // The board keeps whatever it already had.
      }
    };
    void read();
    if (status !== "fielding") return () => { alive = false; };
    const timer = window.setInterval(() => {
      if (!document.hidden) void read();
    }, POLL_MS);
    // Coming back to the tab is the moment a stale board is most obvious.
    const onVisible = (): void => {
      if (!document.hidden) void read();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status, studyId]);

  /**
   * A row lands. The first list animates nothing — never-animate rule 5 forbids
   * anything on first paint — so the very first response only records what is there.
   * After that, exactly the new ids get the entrance, staggered in arrival order.
   */
  const participants = board?.participants ?? null;
  useEffect(() => {
    if (participants === null) return;
    const ids = participants.map((item) => item.id);
    const before = seenRef.current;
    seenRef.current = new Set(ids);
    if (before === null) return;
    const added = ids.filter((id) => !before.has(id));
    if (added.length === 0) return;
    setLanded(added);
    const timer = window.setTimeout(() => setLanded([]), LAND_MS);
    return () => window.clearTimeout(timer);
  }, [participants]);

  const makeLink = useCallback((): void => {
    if (study === null) return;
    setWorking(true);
    setBusy(copy.thinking);
    void (async () => {
      try {
        setInvite(await api.createInvite(study.id, { kind: "open", maxUses: study.targetN }));
        readInvites();
      } catch (err) {
        say(api.describe(err, copy.studyLinkFailed), "error");
      } finally {
        setWorking(false);
        setBusy(null);
      }
    })();
  }, [copy.studyLinkFailed, copy.thinking, readInvites, say, setBusy, study]);

  const copyLink = useCallback(
    (url: string): void => {
      void (async () => {
        try {
          await navigator.clipboard.writeText(url);
          say(copy.studyLinkCopied);
        } catch {
          // Insecure origins and locked-down browsers both land here. The link is
          // already on screen and selectable, which is what the message says.
          say(copy.studyLinkCopyFailed, "warn");
        }
      })();
    },
    [copy.studyLinkCopied, copy.studyLinkCopyFailed, say],
  );

  const toggleTranscript = useCallback(
    (participant: ParticipantRow): void => {
      if (openId === participant.id) {
        setOpenId(null);
        return;
      }
      setOpenId(participant.id);
      if (transcripts[participant.id] !== undefined) return;
      setBusy(copy.thinking);
      void (async () => {
        try {
          const read = await api.getInterview(studyId, participant.id);
          setTranscripts((current) => ({ ...current, [participant.id]: read }));
        } catch (err) {
          setOpenId(null);
          say(api.describe(err, copy.studyTranscriptFailed), "error");
        } finally {
          setBusy(null);
        }
      })();
    },
    [copy.studyTranscriptFailed, copy.thinking, openId, say, setBusy, studyId, transcripts],
  );

  /**
   * Revoke, not delete. The link stops working and every session it minted ends at
   * once, while the interviews it already produced stay exactly as they were: what
   * somebody has said is not retracted by closing the door behind them.
   */
  const revoke = useCallback(
    (id: number): void => {
      setWorking(true);
      void (async () => {
        try {
          const saved = await api.revokeInvite(id);
          setInvites((current) => current.map((item) => (item.id === id ? saved : item)));
        } catch (err) {
          say(api.describe(err, copy.studyLinkRevokeFailed), "error");
        } finally {
          setWorking(false);
        }
      })();
    },
    [copy.studyLinkRevokeFailed, say],
  );

  const setStatus = useCallback(
    (next: "fielding" | "closed"): void => {
      if (study === null) return;
      setWorking(true);
      void (async () => {
        try {
          const saved = await api.updateStudy(study.id, { status: next });
          setStudy(saved);
          onChanged(saved);
        } catch (err) {
          say(api.describe(err, copy.studyUpdateFailed), "error");
        } finally {
          setWorking(false);
        }
      })();
    },
    [copy.studyUpdateFailed, onChanged, say, study],
  );

  if (study === null) {
    // Nothing to draw and nothing to apologise for: the blob is already thinking.
    return (
      <div className="board">
        <button className="board__back label" type="button" onClick={onBack}>
          {copy.studiesLabel}
        </button>
      </div>
    );
  }

  const done = board?.participants.filter((item) => item.status === "completed").length ??
    study.completedCount;

  return (
    <div className="board">
      <button className="board__back label" type="button" onClick={onBack}>
        {copy.studiesLabel}
      </button>

      <p className="board__eyebrow label">{copy.studyGoalHeader}</p>
      <h2 className="board__goal">{study.goal}</h2>

      <p className="board__meta label">
        {copy.studyStatus[study.status]}
        <span aria-hidden="true"> · </span>
        {copy.studyMetaAudience} {copy.studyAudience[study.audience]}
        <span aria-hidden="true"> · </span>
        {copy.studyMetaTime} <span className="num">{copy.studyMinutes(study.timeBudgetMin)}</span>
      </p>

      {/* Progress toward N, as a count and a sentence. Not a bar: there are no
          progress bars in this product, and a bar would imply a precision that
          eleven people cannot carry. */}
      <div className="board__progress">
        <span className="board__count num">{copy.ofN(done, study.targetN)}</span>
        <span className="board__count-sub label">{copy.studyProgressSub}</span>
      </div>
      {board !== null && <p className="board__ready">{copy.studyReady[board.readyReason]}</p>}

      {/* The link. Shown once, and the line under it says why there is no way to
          show it again rather than pretending there is. */}
      <section className="link">
        <h3 className="link__header label">{copy.studyLinkLabel}</h3>
        {invite === null ? (
          <>
            {/* Only where no link has ever been made. Once one has, saying "no link
                yet" over a list of links is a contradiction: the honest line is that
                this one cannot be shown again, which the button already implies. */}
            {invites.length === 0 && <p className="link__none">{copy.studyLinkNone}</p>}
            {/* One accent surface per screen. Before any link exists the link IS the
                point of the board and takes it; afterwards it hands it to Synthesise,
                which is the only other button here that can hold it. */}
            <button
              className={invites.length === 0 ? "btn btn--primary" : "btn btn--quiet"}
              type="button"
              disabled={working}
              onClick={makeLink}
            >
              {invites.length === 0 ? copy.studyLinkMakeCta : copy.studyLinkNewCta}
            </button>
          </>
        ) : (
          <>
            <p className="link__url num">{invite.url}</p>
            <p className="link__once">{copy.studyLinkOnce}</p>
            <div className="link__acts">
              <button
                className="btn btn--primary btn--sm"
                type="button"
                onClick={() => copyLink(invite.url)}
              >
                {copy.studyLinkCopyCta}
              </button>
              <button
                className="btn btn--text"
                type="button"
                onClick={() => {
                  setInvite(null);
                  onInviteDropped();
                }}
              >
                {copy.studyHideCta}
              </button>
              {invite.expiresAt !== null && (
                <span className="link__expires label">
                  {copy.studyLinkExpires(formatDate(invite.expiresAt, locale))}
                </span>
              )}
            </div>
          </>
        )}

        {/* Every link that exists, without its token, so a link sent to the wrong
            list can be shut off. Revoking ends the sessions it minted; it does not
            touch the interviews it already produced. */}
        {invites.length > 0 && (
          <>
            <h4 className="link__list-header label">{copy.studyLinksLabel}</h4>
            <ul className="link__list">
              {invites.map((item) => {
                const dead =
                  item.revokedAt !== null || Date.parse(item.expiresAt) < Date.now();
                return (
                  <li className="link__row" key={item.id}>
                    <span className="link__row-name">
                      {(item.label ?? "").trim().length > 0
                        ? item.label
                        : copy.studyLinkKind[item.kind]}
                    </span>
                    <span className="link__row-meta label">
                      <span className="num">{copy.ofN(item.uses, item.maxUses)}</span>{" "}
                      {copy.studyLinkUsed}
                      <span aria-hidden="true"> · </span>
                      {item.revokedAt !== null
                        ? copy.studyLinkRevoked
                        : dead
                          ? copy.studyLinkExpired
                          : copy.studyLinkExpires(formatDate(item.expiresAt, locale))}
                    </span>
                    {!dead && (
                      <button
                        className="link__revoke label"
                        type="button"
                        disabled={working}
                        onClick={() => revoke(item.id)}
                      >
                        {copy.studyLinkRevokeCta}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {/* The rows. Name or Anonymous, a word for the status, and the one sentence
          the codebook found most distinctive — which is the reason to look. */}
      <section className="board__people">
        <h3 className="board__section label">{copy.studyParticipantsLabel}</h3>

        {board !== null && board.participants.length === 0 && (
          <p className="board__empty">{copy.studyParticipantsEmpty}</p>
        )}

        <ul className="people">
          {(board?.participants ?? []).map((person, index) => {
            const open = openId === person.id;
            const read = transcripts[person.id];
            const landing = landed.indexOf(person.id);
            return (
              <li
                className={landing === -1 ? "person" : "person person--landing"}
                key={person.id}
                style={{ ["--i"]: landing === -1 ? index : landing } as CSSProperties}
              >
                <div className="person__head">
                  <span className="person__name">
                    {(person.displayName ?? "").trim().length > 0
                      ? person.displayName
                      : copy.studyAnonymous}
                  </span>
                  <span className="person__meta label">
                    {copy.participantStatus[person.status]}
                    {person.segment !== null && person.segment.length > 0 && (
                      <>
                        <span aria-hidden="true"> · </span>
                        {person.segment}
                      </>
                    )}
                    <span aria-hidden="true"> · </span>
                    <span className="num">{copy.studyTurnCount(person.turnCount)}</span>
                    <span aria-hidden="true"> · </span>
                    <span className="num">{copy.notesCount(person.findingCount)}</span>
                    {person.durationMs !== null && (
                      <>
                        <span aria-hidden="true"> · </span>
                        <span className="num">
                          {person.durationMs < 60000
                            ? copy.studyDurationShort
                            : copy.studyDuration(person.durationMs)}
                        </span>
                      </>
                    )}
                  </span>
                </div>

                {person.distinctive !== null && person.distinctive.length > 0 && (
                  <p className="person__quote">{person.distinctive}</p>
                )}

                {person.turnCount > 0 && (
                  <button
                    className="person__read label"
                    type="button"
                    aria-expanded={open}
                    onClick={() => toggleTranscript(person)}
                  >
                    {open ? copy.studyHideCta : copy.studyReadCta}
                  </button>
                )}

                {open && read !== undefined && (
                  <ol className="person__transcript" lang={read.language}>
                    {read.messages.map((message, position) => (
                      <li
                        className={
                          message.role === "assistant"
                            ? "person__turn turn--them"
                            : "person__turn turn--you"
                        }
                        key={message.id}
                        style={{ ["--i"]: position } as CSSProperties}
                      >
                        <span className="sr-only">
                          {message.role === "assistant" ? copy.speakerThem : copy.speakerYou}
                        </span>
                        {message.content}
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <Synthesis
        locale={locale}
        studyId={study.id}
        study={study}
        completed={done}
        ready={board?.ready ?? false}
        shell={shell}
      />

      {/* Closing stops anybody new answering. It is one way — a synthesis has been
          run against that set of interviews and reopening would silently change what
          the run was a run of — so it is a quiet text control at the foot rather than
          a button competing with Synthesise, and it disappears once it is done. */}
      {study.status !== "closed" && (
        <div className="board__foot">
          <button
            className="btn btn--text"
            type="button"
            disabled={working}
            onClick={() => setStatus("closed")}
          >
            {copy.studyCloseCta}
          </button>
        </div>
      )}
    </div>
  );
}

/** A date a person reads, in their own language. No strings of its own needed. */
function formatDate(iso: string, locale: Locale): string {
  const when = Date.parse(iso);
  if (Number.isNaN(when)) return "";
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long" }).format(new Date(when));
}
